import PrintResultButton from "@/components/PrintResultButton";
import PrintCoverSheet from "@/components/PrintCoverSheet";
import ToolServiceBacklinks from "@/components/ToolServiceBacklinks";
import { ROUTE_SERVICE_LINKS, CORE_SERVICE_LINKS } from "@/data/tool-service-links";
// /tools/timeline — ตัวประเมินระยะเวลาและลำดับขั้น (โดยประมาณ)
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { organizationSchema, speakableSchema, faqPageSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { DESTINATIONS, ROUTE_LABELS, buildTimeline, sumDays, type Route as ChainRoute } from "@/data/service-planner";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/tools/timeline";
const TITLE = "ตัวประเมินระยะเวลารับรองเอกสาร — รู้ล่วงหน้าว่าใช้กี่วันทำการ";
const DESC =
  "ประเมินช่วงวันทำการโดยประมาณของทุกขั้นตอน ตั้งแต่ขอเอกสารต้นทาง แปลรับรอง กรมการกงสุล Apostille หรือสถานทูต จนถึงการจัดส่งเอกสารตัวจริง";

const FAQ = [
  {
    q: "ระยะเวลาที่แสดงเป็นการรับประกันหรือไม่",
    a: "ไม่ใช่การรับประกัน ตัวเลขเป็นช่วงวันทำการโดยประมาณจากคิวที่พบจริงของแต่ละหน่วยงาน ระยะเวลาจริงขึ้นกับคิวของกรมการกงสุล สถานทูตปลายทาง ปริมาณเอกสาร ความครบถ้วนของต้นฉบับ และวันหยุดราชการของทั้งไทยและประเทศปลายทาง",
  },
  {
    q: "ขั้นตอนไหนใช้เวลานานที่สุด",
    a: "โดยทั่วไปคือหนังสือรับรองความประพฤติจากกองทะเบียนประวัติอาชญากร ซึ่งใช้เวลาประมาณ 20–45 วันทำการ รองลงมาคือการรับรองที่สถานทูตบางประเทศที่มีรอบรับ-คืนเอกสารจำกัด จึงควรเริ่มสองขั้นนี้ก่อนเป็นอันดับแรก",
  },
  {
    q: "ทำงานเร่งด่วนได้หรือไม่",
    a: "งานแปลและงานรับรองลายมือชื่อโดยทนายผู้ทำคำรับรองสามารถเร่งได้ภายในวันเดียวในหลายกรณี ส่วนขั้นตอนของหน่วยงานราชการขึ้นกับบริการด่วนที่หน่วยงานนั้นเปิดให้ ซึ่งไม่สามารถเร่งเกินกรอบที่หน่วยงานกำหนดได้",
  },
  {
    q: "เส้นทาง Apostille เร็วกว่าเส้นทางสถานทูตเท่าไร",
    a: "เส้นทาง Apostille มักจบเร็วกว่าเพราะจบที่การรับรองของกรมการกงสุลแล้วขอ Apostille เพิ่มอีก 1–3 วันทำการ ขณะที่เส้นทางสถานทูตต้องรอคิวของสถานทูตปลายทางอีกประมาณ 3–15 วันทำการ ขึ้นกับแต่ละสถานทูต",
  },
];

export const Route = createFileRoute("/tools/timeline")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "th") },
      { name: "description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "th") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: canonicalAndHreflang(PATH),
    scripts: [
      consolidateGraph([
        organizationSchema(),
        speakableSchema([".aeo-speakable", "h1", "h2"]),
        faqPageSchema(FAQ),
      ]),
    ],
  }),
  component: TimelinePage,
});

function TimelinePage() {
  const [dest, setDest] = useState("us");
  const [needPolice, setNeedPolice] = useState(false);
  const [needNotary, setNeedNotary] = useState(false);
  const [shipAbroad, setShipAbroad] = useState(false);

  const chainRoute: ChainRoute = useMemo(
    () => (DESTINATIONS.find((d) => d.code === dest) ?? DESTINATIONS[0]).route,
    [dest],
  );
  const steps = useMemo(
    () => buildTimeline({ route: chainRoute, needPolice, needNotary, shipAbroad }),
    [chainRoute, needPolice, needNotary, shipAbroad],
  );
  const total = sumDays(steps);
  const destName = (DESTINATIONS.find((d) => d.code === dest) ?? DESTINATIONS[0]).name;

  return (
    <main className="mx-auto w-full max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:underline">หน้าแรก</Link> /{" "}
        <Link to="/tools" className="hover:underline">เครื่องมือช่วยตัดสินใจ</Link> / ตัวประเมินระยะเวลา
      </nav>
      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">ตัวประเมินระยะเวลาและลำดับขั้น</h1>
      <p className="aeo-speakable mt-3 text-muted-foreground">
        เลือกประเทศปลายทางและเงื่อนไขของงาน ระบบจะเรียงลำดับขั้นตอนพร้อมช่วงวันทำการโดยประมาณ
        ตัวเลขนี้เป็นการประเมินจากคิวที่พบจริง ไม่ใช่การรับประกันผล
      </p>

      <section className="mt-8 space-y-4 rounded-xl border border-border bg-card p-5" data-print-hide>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">ประเทศปลายทาง</span>
          <select
            value={dest}
            onChange={(e) => setDest(e.target.value)}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm md:max-w-sm"
            aria-label="เลือกประเทศปลายทาง"
          >
            {DESTINATIONS.map((d) => (
              <option key={d.code} value={d.code}>{d.name}</option>
            ))}
          </select>
        </label>
        <p className="text-sm text-muted-foreground">เส้นทาง: <strong>{ROUTE_LABELS[chainRoute]}</strong></p>
        <div className="grid gap-2 md:grid-cols-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={needPolice} onChange={(e) => setNeedPolice(e.target.checked)} />
            ต้องมีหนังสือรับรองความประพฤติ
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={needNotary} onChange={(e) => setNeedNotary(e.target.checked)} />
            มีเอกสารเอกชนที่ต้องให้ทนายรับรองลายมือชื่อ
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={shipAbroad} onChange={(e) => setShipAbroad(e.target.checked)} />
            ต้องจัดส่งเอกสารตัวจริง
          </label>
        </div>
      </section>

      <div className="mt-6 flex flex-wrap gap-3" data-print-hide>
        <PrintResultButton
          label="ดาวน์โหลดลำดับขั้นเป็น PDF"
          fileName={`NYC-ไทม์ไลน์รับรองเอกสาร-${destName}-${total.min}-${total.max}วันทำการ`}
          fileNameEn={`NYC-Legalisation-Timeline-${dest}-${total.min}-${total.max}-business-days`}
        />
      </div>

      <PrintCoverSheet
        title="ลำดับขั้นและระยะเวลาโดยประมาณ"
        purpose="วางแผนระยะเวลาการรับรองเอกสาร"
        destination={destName}
        routeLabel={ROUTE_LABELS[chainRoute]}
        summary={`รวมโดยประมาณ ${total.min}–${total.max} วันทำการ (ไม่รวมวันหยุดราชการ) · ${steps.length} ขั้นตอน`}
        checklist={steps.map((s) => `${s.label} — ${s.minDays}–${s.maxDays} วันทำการ`)}
      />
      <section className="mt-8">
        <h2 className="text-xl font-semibold">ลำดับขั้นและระยะเวลาโดยประมาณ</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="py-2 pr-3">ลำดับ</th>
                <th className="py-2 pr-3">ขั้นตอน</th>
                <th className="py-2 pr-3">วันทำการ (โดยประมาณ)</th>
                <th className="py-2">รายละเอียด</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((s, i) => (
                <tr key={s.key} className="border-b border-border/60 align-top">
                  <td className="py-2 pr-3">{i + 1}</td>
                  <td className="py-2 pr-3 font-medium">{s.label}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{s.minDays}–{s.maxDays}</td>
                  <td className="py-2 text-muted-foreground">{s.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 rounded-lg bg-muted/50 p-4 text-sm">
          รวมโดยประมาณ <strong>{total.min}–{total.max} วันทำการ</strong> (ไม่รวมวันหยุดราชการ
          และขึ้นกับคิวของแต่ละหน่วยงาน)
        </p>
      </section>

      <section className="mt-10">
        <h2 className="text-xl font-semibold">คำถามที่พบบ่อย</h2>
        <div className="mt-3 space-y-4">
          {FAQ.map((f) => (
            <details key={f.q} className="rounded-lg border border-border bg-card p-4">
              <summary className="cursor-pointer text-sm font-medium">{f.q}</summary>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <ToolServiceBacklinks
        heading={`บริการที่ตรงกับเส้นทาง ${ROUTE_LABELS[chainRoute]}`}
        lead="กดเข้าไปดูขั้นตอน เอกสารที่ต้องเตรียม และข้อควรระวังของแต่ละบริการที่อยู่ในไทม์ไลน์ด้านบน"
        links={[...(ROUTE_SERVICE_LINKS[chainRoute] ?? []), ...CORE_SERVICE_LINKS]}
      />

      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/tools/document-advisor" className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
          ดูรายการเอกสารที่ต้องเตรียม
        </Link>
        <Link to="/compare/service-types" className="rounded-lg border border-border px-4 py-2 text-sm font-semibold">
          เทียบ Notary / แปลรับรอง / NAATI / MFA
        </Link>
      </div>
    </main>
  );
}
